/**
 * The firm's previous customers, carried over.
 *
 * Two steps, and the first one writes nothing. An office manager pointing an importer at
 * their entire customer base deserves to see the rejects before anything is written — and
 * because the preview runs the same code as the commit, what it shows is what will happen
 * rather than an estimate of it.
 *
 * The commit button deliberately does not appear until a preview has been run. Not to be
 * strict: it is the one screen in the product where a mistaken click is measured in tens of
 * thousands of rows.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorKey, type ApiError } from '@taxi/api-client';

import { importApi, importFormats, type ImportFormat, type ImportReport } from '../api';
import { describe } from '../errors';
import { useTenantFormats } from '../../shell/formats';
import {
  Banner,
  Button,
  Count,
  FileDrop,
  Input,
  PageHeader,
  Section,
  Select,
  SettingRow,
  StatTile,
  Table,
  Td,
  Th,
  Tr,
  type Tone,
} from '../../ui';

export function ImportPage() {
  const { t } = useTranslation();

  const [format, setFormat] = useState<ImportFormat>('csv');
  const [file, setFile] = useState<File | null>(null);
  const [areaCode, setAreaCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);


  async function run(commit: boolean) {
    if (file === null) return;

    setBusy(true);
    setProblem(null);

    try {
      setReport(await importApi.run(format, file, commit, areaCode.trim() || null));
    } catch (error) {
      setReport(null);
      setProblem(describe(error, t, 'import.couldNotRun'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title={t('nav.settings.import')} description={t('nav.settingsAbout.import')} />

      <Section title={t('import.title')} description={t('import.intro')}>
        {problem !== null && (
          <div className="px-5 pt-3">
            <Banner tone="danger">{problem}</Banner>
          </div>
        )}

        <SettingRow label={t('import.format')} help={t(`import.formatHint.${format}`)}>
          <Select
            aria-label={t('import.format')}
            value={format}
            onChange={(event) => {
              setFormat(event.target.value as ImportFormat);
              setReport(null);
            }}
          >
            {importFormats.map((name) => (
              <option key={name} value={name}>
                {t(`import.formats.${name}`)}
              </option>
            ))}
          </Select>
        </SettingRow>

        <SettingRow label={t('import.file')}>
          <FileDrop
            file={file}
            disabled={busy}
            onFile={(chosen) => {
              setFile(chosen);

              // A new file makes the previous report meaningless, and a stale one beside a
              // commit button is how somebody commits the wrong thing.
              setReport(null);
            }}
          />
        </SettingRow>

        <SettingRow label={t('import.areaCode')} help={t('import.areaCodeHint')} last>
          <Input
            aria-label={t('import.areaCode')}
            value={areaCode}
            onChange={(event) => setAreaCode(event.target.value)}
            placeholder="058"
            inputMode="numeric"
          />
        </SettingRow>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-rule">
          {/* Only after a preview, and only when there is something to write. */}
          {report !== null && !report.committed && report.read > 0 && (
            <Button variant="danger" disabled={busy} onClick={() => void run(true)}>
              {t('import.commit')}
            </Button>
          )}

          <Button
            variant="primary"
            disabled={file === null || busy}
            onClick={() => void run(false)}
          >
            {busy ? t('import.working') : t('import.preview')}
          </Button>
        </footer>
      </Section>

      {report !== null && <Report report={report} />}
    </>
  );
}

/**
 * What the run did, or would have done.
 *
 * The counts are tiles rather than a table because they are read once, at a glance, to
 * decide whether to commit — a table of seven rows makes the reader compare numbers that
 * have nothing to do with each other. The rejects, which are compared row against row, are
 * the table.
 */
function Report({ report }: { report: ImportReport }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const counts: { key: string; value: number; tone?: Tone }[] = [
    { key: 'read', value: report.read },
    { key: 'customersCreated', value: report.customersCreated },
    { key: 'customersMatched', value: report.customersMatched },
    { key: 'addressesAdded', value: report.addressesAdded },
    { key: 'addressesAlreadyKnown', value: report.addressesAlreadyKnown },

    // Deleted from Адреси за заключване since an earlier import, and left out again — shown
    // only when there are some, as most firms will never delete one.
    ...(report.addressesRemovedEarlier > 0
      ? [{ key: 'addressesRemovedEarlier', value: report.addressesRemovedEarlier }]
      : []),
    { key: 'skipped', value: report.skipped },

    // The one figure the reader is looking for, coloured only when there is something to
    // look at: a red zero teaches nobody anything.
    {
      key: 'rejected',
      value: report.rejects.length,
      tone: report.rejects.length > 0 ? 'danger' : undefined,
    },
  ];

  return (
    <>
      <Section title={report.committed ? t('import.done') : t('import.wouldDo')}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1 p-2">
          {counts.map((count) => (
            <StatTile
              key={count.key}
              label={t(`import.count.${count.key}`)}
              value={formats.count(count.value)}
              tone={count.tone}
            />
          ))}
        </div>
      </Section>

      {report.rejects.length > 0 && (
        <Section
          title={t('import.rejects')}
          description={report.truncated ? t('import.truncated') : undefined}
          actions={<Count>{formats.count(report.rejects.length)}</Count>}
        >
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th numeric>{t('import.row')}</Th>
                  <Th>{t('import.reason')}</Th>
                  <Th>{t('import.value')}</Th>
                </tr>
              </thead>
              <tbody>
                {report.rejects.map((reject) => (
                  <Tr key={`${reject.rowNumber}-${reject.code}`}>
                    {/* The line in the customer's own file, not a quantity: unformatted, so
                        that searching their CSV for it finds it. The counts above are. */}
                    <Td numeric>{reject.rowNumber}</Td>

                    {/* Translated from the code the server sent, like every other refusal.
                        An English reject list in a Bulgarian office is half a report. */}
                    <Td wrap>{t(errorKey({ code: reject.code } as ApiError['problem']))}</Td>

                    <Td wrap>
                      <code>{reject.value}</code>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Section>
      )}
    </>
  );
}
